import { FormEvent, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { AccountRequestSuccessView } from "../components/AccountRequestSuccessView";
import { ClaimFieldRow } from "../components/ClaimFieldRow";
import {
  IconConcept,
  IconLocation,
  IconMail,
  IconMailConfirm,
  IconPerson,
  IconRegistrationType,
  IconRole,
} from "../components/ClaimFormIcons";
import * as claimApi from "../api";
import { inferEmployeeTypeForCatalogRole, inferUserTypeForCatalogRole } from "../lib/claimRoleInference";
import { isValidSignInEmail } from "../lib/validation";
import type { StoreInfo } from "../types";
import SelfRegistrationGatePage from "./SelfRegistrationGatePage";
import type { SelfRegistrationGateReason } from "./SelfRegistrationGatePage";

/** Official Workvivo mobile app listings (Workvivo Limited). */
const WORKVIVO_APP_APPLE =
  "https://apps.apple.com/app/workvivo/id1322500684";
const WORKVIVO_APP_GOOGLE =
  "https://play.google.com/store/apps/details?id=com.workvivo.android";

type FormStatus = "idle" | "submitting" | "success" | "error";
type FieldName = "firstName" | "lastName" | "email" | "emailConfirm" | "infoConfirmed";

function emailsMatch(primary: string, confirm: string): boolean {
  return primary.trim().toLowerCase() === confirm.trim().toLowerCase();
}

/** Matches slice store ids from provisioning (`TM-*`, `R-*`, `CA-*` college ambassadors). */
type RegistrationStreamFilter = "" | "team_member" | "resident" | "college_ambassador";

function filteredStoresForRegistrationStream(stores: StoreInfo[], stream: RegistrationStreamFilter): StoreInfo[] {
  if (!stream) return [];
  return stores
    .filter((s) => {
      const id = s.store_id.trim().toLowerCase();
      if (stream === "team_member") return id.startsWith("tm-");
      if (stream === "resident") return id.startsWith("r-");
      if (stream === "college_ambassador") return id.startsWith("ca-");
      return false;
    })
    .sort((a, b) => a.business_name.localeCompare(b.business_name));
}

/** Catalog role row for Latitude Margaritaville Residents (MargaritavilleRolesList). */
const RESIDENT_CATALOG_JOB_TITLE = "Resident";

function findStoreCaseInsensitive(list: StoreInfo[], raw: string): StoreInfo | undefined {
  const t = raw.trim().toLowerCase();
  return list.find((s) => s.store_id.trim().toLowerCase() === t);
}

function inferStreamFromStoreId(storeId: string): RegistrationStreamFilter {
  const id = storeId.trim().toLowerCase();
  if (id.startsWith("tm-")) return "team_member";
  if (id.startsWith("r-")) return "resident";
  if (id.startsWith("ca-")) return "college_ambassador";
  return "";
}

function conceptLabelForStore(s: StoreInfo): string {
  return (s.catalog_concept?.trim() || s.business_name || "").trim();
}

function locationLabelForStore(s: StoreInfo): string {
  return (s.catalog_location?.trim() || s.business_name || "").trim();
}

/** Labels for the self-registration gate “details” panel when we have a store row. */
function gateDetailLabelsFromStore(s: StoreInfo): { concept: string | null; location: string | null } {
  return {
    concept: s.catalog_concept?.trim() || conceptLabelForStore(s) || null,
    location: s.catalog_location?.trim() || s.business_name?.trim() || null,
  };
}

/** Root short URLs only: /TM-*, /R-*, /CA-* — anything else falls through to /form. */
function looksLikeSliceStoreId(id: string): boolean {
  const t = id.trim().toLowerCase();
  return t.startsWith("tm-") || t.startsWith("r-") || t.startsWith("ca-");
}

function AccountRequestFormPage() {
  const { presetStoreId: rawFormPreset, storeId: rawRootStoreId } = useParams<{
    presetStoreId?: string;
    storeId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  const decodedRoot = useMemo(() => {
    if (!rawRootStoreId?.trim()) return "";
    try {
      return decodeURIComponent(rawRootStoreId).trim();
    } catch {
      return rawRootStoreId.trim();
    }
  }, [rawRootStoreId]);

  const invalidRootStoreSegment = useMemo(
    () => Boolean(rawRootStoreId?.trim()) && Boolean(decodedRoot) && !looksLikeSliceStoreId(decodedRoot),
    [rawRootStoreId, decodedRoot]
  );

  const presetStoreId = useMemo(() => {
    if (invalidRootStoreSegment) return undefined;
    const raw = rawFormPreset?.trim() ? rawFormPreset : rawRootStoreId?.trim() ? rawRootStoreId : "";
    if (!raw.trim()) return undefined;
    try {
      return decodeURIComponent(raw).trim();
    } catch {
      return raw.trim();
    }
  }, [invalidRootStoreSegment, rawFormPreset, rawRootStoreId]);

  /** Canonicalize invite paths (tm-/r-/ca-) to lowercase so links match regardless of casing. */
  useLayoutEffect(() => {
    if (!presetStoreId?.trim()) return;
    if (!looksLikeSliceStoreId(presetStoreId)) return;
    const id = presetStoreId.trim();
    const lower = id.toLowerCase();
    if (id === lower) return;
    const enc = encodeURIComponent(lower);
    const suffix = `${location.search}${location.hash}`;
    if (rawFormPreset?.trim()) {
      navigate(`/form/${enc}${suffix}`, { replace: true });
    } else if (rawRootStoreId?.trim()) {
      navigate(`/${enc}${suffix}`, { replace: true });
    }
  }, [
    presetStoreId,
    rawFormPreset,
    rawRootStoreId,
    navigate,
    location.search,
    location.hash,
  ]);

  const [stores, setStores] = useState<StoreInfo[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storesError, setStoresError] = useState<string | null>(null);
  const [registrationStream, setRegistrationStream] = useState<RegistrationStreamFilter>("");
  const [storeId, setStoreId] = useState<string>("");
  /** When catalog facets exist, pick concept then location (store_id). */
  const [conceptKey, setConceptKey] = useState("");
  type PresetGate = "idle" | "resolving" | "ready" | "blocked";
  const [presetGate, setPresetGate] = useState<PresetGate>(() => (presetStoreId ? "resolving" : "idle"));
  const [blockReason, setBlockReason] = useState<SelfRegistrationGateReason | null>(null);
  const [blockConceptLabel, setBlockConceptLabel] = useState<string | null>(null);
  const [blockLocationLabel, setBlockLocationLabel] = useState<string | null>(null);
  /** Exact role strings from `/catalog/margaritaville/roles-by-store` (MargaritavilleRolesList). */
  const [catalogRoles, setCatalogRoles] = useState<string[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [employeeType, setEmployeeType] = useState<"hourly" | "salaried_manager">("hourly");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [infoConfirmed, setInfoConfirmed] = useState(false);
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [touched, setTouched] = useState<Record<FieldName, boolean>>({
    firstName: false,
    lastName: false,
    email: false,
    emailConfirm: false,
    infoConfirmed: false,
  });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setStoresLoading(true);
      setStoresError(null);
      try {
        const dbStores = await claimApi.listSelfRegistrationStores();
        if (cancelled) return;
        setStores(dbStores);
      } catch (e) {
        if (cancelled) return;
        setStoresError(e instanceof Error ? e.message : "Failed to load locations.");
      } finally {
        if (!cancelled) setStoresLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredStores = useMemo(
    () => filteredStoresForRegistrationStream(stores, registrationStream),
    [stores, registrationStream]
  );

  const useCascade = useMemo(
    () =>
      Boolean(registrationStream) &&
      filteredStores.some((s) => Boolean(s.catalog_concept?.trim() || s.catalog_location?.trim())),
    [registrationStream, filteredStores]
  );

  const conceptOptions = useMemo(() => {
    if (!useCascade) return [] as string[];
    const labels: string[] = [];
    const seen = new Set<string>();
    for (const s of filteredStores) {
      const label = conceptLabelForStore(s);
      if (!label) continue;
      const key = label.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        labels.push(label);
      }
    }
    return labels.sort((a, b) => a.localeCompare(b));
  }, [filteredStores, useCascade]);

  const storesForConcept = useMemo(() => {
    if (!useCascade || !conceptKey.trim()) return [] as StoreInfo[];
    const ck = conceptKey.trim().toLowerCase();
    return filteredStores.filter((s) => conceptLabelForStore(s).toLowerCase() === ck);
  }, [filteredStores, useCascade, conceptKey]);

  useEffect(() => {
    if (!registrationStream) {
      setStoreId("");
      return;
    }
    if (useCascade) {
      return;
    }
    setStoreId((prev) => {
      const stillValid = filteredStores.some((s) => s.store_id === prev);
      if (stillValid) return prev;
      return filteredStores[0]?.store_id ?? "";
    });
  }, [registrationStream, filteredStores, useCascade]);

  useEffect(() => {
    if (!useCascade) {
      setConceptKey("");
      return;
    }
    setConceptKey((prev) => {
      const match = conceptOptions.find((c) => c.toLowerCase() === prev.trim().toLowerCase());
      if (match) return match;
      return conceptOptions[0] ?? "";
    });
  }, [useCascade, conceptOptions]);

  useEffect(() => {
    if (!useCascade) {
      return;
    }
    setStoreId((prev) => {
      const stillValid = storesForConcept.some((s) => s.store_id === prev);
      if (stillValid) return prev;
      return storesForConcept[0]?.store_id ?? "";
    });
  }, [useCascade, storesForConcept]);

  // Declared after the stream/concept/store reset effects on purpose: effects run in declaration
  // order, so when the store list arrives the store from the link (/tm-r-mv-tulsa) is applied last
  // and is not wiped by those resets, which still see the previous (empty) stream in that commit.
  useEffect(() => {
    if (!presetStoreId) {
      setPresetGate("idle");
      setBlockReason(null);
      setBlockConceptLabel(null);
      setBlockLocationLabel(null);
      return;
    }

    if (storesLoading) {
      setPresetGate("resolving");
      return;
    }

    const direct = findStoreCaseInsensitive(stores, presetStoreId);
    if (direct) {
      const stream = inferStreamFromStoreId(direct.store_id);
      if (!stream) {
        const m = gateDetailLabelsFromStore(direct);
        setPresetGate("blocked");
        setBlockReason("unknown");
        setBlockConceptLabel(m.concept);
        setBlockLocationLabel(m.location);
        return;
      }
      setRegistrationStream(stream);
      setStoreId(direct.store_id);
      setConceptKey(conceptLabelForStore(direct) || direct.business_name);
      setPresetGate("ready");
      setBlockReason(null);
      setBlockConceptLabel(null);
      setBlockLocationLabel(null);
      return;
    }

    let cancelled = false;
    setPresetGate("resolving");

    void claimApi
      .lookupStore(presetStoreId)
      .then((res) => {
        if (cancelled) return;

        if (!res.valid && res.store && res.store.is_active === false) {
          const m = gateDetailLabelsFromStore(res.store);
          setPresetGate("blocked");
          setBlockReason("inactive");
          setBlockConceptLabel(m.concept);
          setBlockLocationLabel(m.location);
          return;
        }

        if (!res.valid && !res.store) {
          setPresetGate("blocked");
          setBlockReason("unknown");
          setBlockConceptLabel(null);
          setBlockLocationLabel(null);
          return;
        }

        if (res.valid && res.store) {
          const pm = res.store.provisioning_method ?? "";
          if (pm !== "self_registration") {
            const m = gateDetailLabelsFromStore(res.store);
            setPresetGate("blocked");
            setBlockReason("not_self_registration");
            setBlockConceptLabel(m.concept);
            setBlockLocationLabel(m.location);
            return;
          }

          const inList = findStoreCaseInsensitive(stores, res.store.store_id);
          if (!inList) {
            const m = gateDetailLabelsFromStore(res.store);
            setPresetGate("blocked");
            setBlockReason("not_self_registration");
            setBlockConceptLabel(m.concept);
            setBlockLocationLabel(m.location);
            return;
          }

          const stream = inferStreamFromStoreId(res.store.store_id);
          if (!stream) {
            const m = gateDetailLabelsFromStore(res.store);
            setPresetGate("blocked");
            setBlockReason("unknown");
            setBlockConceptLabel(m.concept);
            setBlockLocationLabel(m.location);
            return;
          }

          setRegistrationStream(stream);
          setStoreId(res.store.store_id);
          setConceptKey(conceptLabelForStore(res.store) || res.store.business_name);
          setPresetGate("ready");
          setBlockReason(null);
          setBlockConceptLabel(null);
          setBlockLocationLabel(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPresetGate("blocked");
          setBlockReason("unknown");
          setBlockConceptLabel(null);
          setBlockLocationLabel(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [presetStoreId, storesLoading, stores]);


  useEffect(() => {
    if (!storeId.trim()) {
      setCatalogRoles([]);
      setSelectedRole("");
      setRolesError(null);
      setRolesLoading(false);
      return;
    }
    if (registrationStream === "resident") {
      setRolesLoading(false);
      setRolesError(null);
      setCatalogRoles([RESIDENT_CATALOG_JOB_TITLE]);
      setSelectedRole(RESIDENT_CATALOG_JOB_TITLE);
      return;
    }
    let cancelled = false;
    setRolesLoading(true);
    setRolesError(null);
    void claimApi
      .fetchRolesByStore(storeId)
      .then((data) => {
        if (cancelled) return;
        const list = [...data.roles];
        setCatalogRoles(list);
        setSelectedRole((prev) => (prev && list.includes(prev) ? prev : list[0] ?? ""));
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setCatalogRoles([]);
          setSelectedRole("");
          setRolesError(e instanceof Error ? e.message : "Failed to load roles for this location.");
        }
      })
      .finally(() => {
        if (!cancelled) setRolesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [storeId, registrationStream]);

  const selectedStore =
    filteredStores.find((s) => s.store_id === storeId) ?? stores.find((s) => s.store_id === storeId) ?? null;
  const inferredUserType =
    selectedRole.trim() !== "" ? inferUserTypeForCatalogRole() : "store_employee";

  useEffect(() => {
    if (!registrationStream || !storeId.trim()) {
      setEmployeeType("hourly");
      return;
    }
    if (registrationStream === "resident") {
      setEmployeeType("hourly");
      return;
    }
    if (!selectedRole.trim() || !catalogRoles.includes(selectedRole)) {
      setEmployeeType("hourly");
      return;
    }
    setEmployeeType(inferEmployeeTypeForCatalogRole(selectedRole));
  }, [registrationStream, storeId, selectedRole, catalogRoles]);

  const validation = useMemo(() => {
    const problems: Array<{ field: FieldName; message: string }> = [];

    if (!firstName.trim()) {
      problems.push({ field: "firstName", message: "First name is required." });
    }
    if (!lastName.trim()) {
      problems.push({ field: "lastName", message: "Last name is required." });
    }
    if (!email.trim()) {
      problems.push({ field: "email", message: "Preferred sign-in email is required." });
    } else if (!isValidSignInEmail(email)) {
      problems.push({ field: "email", message: "Enter a valid email address (example: name@example.com)." });
    }
    if (!confirmEmail.trim()) {
      problems.push({ field: "emailConfirm", message: "Re-enter your email to confirm." });
    } else if (!emailsMatch(email, confirmEmail)) {
      problems.push({
        field: "emailConfirm",
        message: "This must exactly match your preferred sign-in email.",
      });
    }
    if (!infoConfirmed) {
      problems.push({ field: "infoConfirmed", message: "Please confirm the information is correct." });
    }

    return {
      problems,
      messagesByField: problems.reduce<Record<FieldName, string | undefined>>((acc, p) => {
        acc[p.field] = p.message;
        return acc;
      }, {
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        emailConfirm: undefined,
        infoConfirmed: undefined,
      }),
    };
  }, [confirmEmail, email, firstName, infoConfirmed, lastName]);

  const showErrorFor = (field: FieldName) => submitAttempted || touched[field];
  const fieldError = (field: FieldName) => (showErrorFor(field) ? validation.messagesByField[field] : undefined);

  const canSubmit =
    Boolean(firstName.trim()) &&
    Boolean(lastName.trim()) &&
    isValidSignInEmail(email) &&
    confirmEmail.trim() !== "" &&
    emailsMatch(email, confirmEmail) &&
    infoConfirmed &&
    registrationStream !== "" &&
    Boolean(selectedStore) &&
    Boolean(selectedRole.trim()) &&
    catalogRoles.includes(selectedRole) &&
    !rolesLoading &&
    !rolesError;

  /** Invite / deep-link: registration type + concept + location are fixed. */
  const lockPresetPickers = Boolean(presetStoreId) && presetGate === "ready";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSubmitAttempted(true);
    setTouched((prev) => ({
      ...prev,
      firstName: true,
      lastName: true,
      email: true,
      emailConfirm: true,
      infoConfirmed: true,
    }));
    if (!registrationStream) {
      setErrorMessage("Choose whether you are a team member, resident, or college ambassador.");
      return;
    }
    if (!selectedStore || !selectedRole.trim() || !catalogRoles.includes(selectedRole)) {
      setErrorMessage("Please select a location and a role.");
      return;
    }
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setErrorMessage("Please enter your first name, last name, and preferred sign-in email.");
      return;
    }
    if (!isValidSignInEmail(email)) {
      setErrorMessage("Please enter a valid email address for your preferred sign-in email.");
      return;
    }
    if (!confirmEmail.trim()) {
      setErrorMessage("Please re-enter your preferred sign-in email to confirm.");
      return;
    }
    if (!emailsMatch(email, confirmEmail)) {
      setErrorMessage("Confirmed email must match your preferred sign-in email.");
      return;
    }
    if (!infoConfirmed) {
      setErrorMessage("Please confirm that all the information you have entered is correct.");
      return;
    }

    setStatus("submitting");
    try {
      const response = await claimApi.submitSelfOnboard({
        store_id: selectedStore.store_id,
        user_type: inferredUserType,
        employee_type: employeeType,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim().toLowerCase(),
        champion: false,
        job_title: selectedRole,
        location_label: locationLabelForStore(selectedStore),
      });
      if (response.success) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMessage(response.message || "Request failed.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  if (invalidRootStoreSegment) {
    return <Navigate to="/form" replace />;
  }

  if (presetGate === "blocked" && blockReason === "unknown") {
    return <Navigate to="/form" replace />;
  }

  if (presetGate === "blocked" && blockReason) {
    return (
      <SelfRegistrationGatePage
        reason={blockReason}
        storeIdHint={presetStoreId ?? ""}
        conceptLabel={blockConceptLabel}
        locationLabel={blockLocationLabel}
      />
    );
  }

  if (presetStoreId && presetGate === "resolving") {
    return (
      <div className="claim-layout claim-form-page">
        <header className="claim-hero" aria-label="Coconut Telegraph" />
        <div className="claim-body">
          <div className="claim-card claim-card--wide">
            <p className="claim-muted" style={{ margin: 0, textAlign: "center", padding: "2rem 1rem" }}>
              Checking your registration link…
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="claim-layout claim-form-page">
      <header className="claim-hero" aria-label="Coconut Telegraph" />

      <div className="claim-body">
        <div className="claim-card claim-card--wide">
          {status === "success" ? (
            <AccountRequestSuccessView
              email={email}
              locationLabel={
                selectedStore?.business_name ?? "your selected location"
              }
              workvivoAppStoreUrl={WORKVIVO_APP_APPLE}
              workvivoPlayStoreUrl={WORKVIVO_APP_GOOGLE}
            />
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <div className="claim-form-intro">
                  <div className="claim-form-intro-accent" aria-hidden>
                    🌴
                  </div>
                  <div className="claim-form-intro-copy">
                    <h2 className="claim-form-intro-title">Coconut Telegraph - Account Request</h2>
                    <p className="claim-form-intro-body">
                      Request access to <strong>Coconut Telegraph</strong>. You will receive an email when your account
                      is ready. Fill out this form below and choose <strong>Submit request</strong>.
                    </p>
                  </div>
                </div>

                {lockPresetPickers ? (
                  <p className="claim-muted" style={{ margin: 0 }} id="ct-preset-locked-hint">
                    Registration type and location are set from your invite link. Choose your role below, then complete
                    your details.
                  </p>
                ) : null}

                <ClaimFieldRow icon={<IconRegistrationType />}>
                  <label className="claim-field-stack" htmlFor="ct-reg-stream">
                    <span className="claim-field-label">I am registering as</span>
                    <select
                      id="ct-reg-stream"
                      className={`claim-select${lockPresetPickers ? " claim-select--preset-locked" : ""}`}
                      value={registrationStream}
                      onChange={(e) => setRegistrationStream(e.target.value as RegistrationStreamFilter)}
                      disabled={storesLoading || lockPresetPickers}
                      aria-label="Registration type"
                      aria-describedby={lockPresetPickers ? "ct-preset-locked-hint" : undefined}
                    >
                    <option value="">Choose one…</option>
                    <option value="team_member">Team member</option>
                    <option value="resident">Resident</option>
                    <option value="college_ambassador">College ambassador</option>
                  </select>
                  </label>
                </ClaimFieldRow>

                {useCascade ? (
                  <>
                    <ClaimFieldRow icon={<IconConcept />}>
                      <label className="claim-field-stack" htmlFor="ct-concept">
                        <span className="claim-field-label">Concept</span>
                        <select
                          id="ct-concept"
                          className={`claim-select${lockPresetPickers ? " claim-select--preset-locked" : ""}`}
                          value={conceptOptions.includes(conceptKey) ? conceptKey : conceptOptions[0] ?? ""}
                          onChange={(e) => setConceptKey(e.target.value)}
                          disabled={
                            storesLoading ||
                            lockPresetPickers ||
                            !registrationStream ||
                            filteredStores.length === 0 ||
                            stores.length === 0 ||
                            conceptOptions.length === 0
                          }
                          aria-label="Concept or property"
                          aria-describedby={lockPresetPickers ? "ct-preset-locked-hint" : undefined}
                        >
                        {!registrationStream ? (
                          <option value="">Select above first</option>
                        ) : conceptOptions.length === 0 ? (
                          <option value="">No concepts available</option>
                        ) : (
                          conceptOptions.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))
                        )}
                      </select>
                      </label>
                    </ClaimFieldRow>
                    <ClaimFieldRow icon={<IconLocation />}>
                      <label className="claim-field-stack" htmlFor="ct-location-cascade">
                        <span className="claim-field-label">Location</span>
                        <select
                          id="ct-location-cascade"
                          className={`claim-select${lockPresetPickers ? " claim-select--preset-locked" : ""}`}
                          value={storeId}
                          onChange={(e) => setStoreId(e.target.value)}
                          disabled={
                            storesLoading ||
                            lockPresetPickers ||
                            !registrationStream ||
                            storesForConcept.length === 0 ||
                            stores.length === 0
                          }
                          aria-label="Location"
                          aria-describedby={lockPresetPickers ? "ct-preset-locked-hint" : undefined}
                        >
                        {!registrationStream ? (
                          <option value="">Select above first</option>
                        ) : storesForConcept.length === 0 ? (
                          <option value="">No locations for this concept</option>
                        ) : (
                          storesForConcept.map((s) => (
                            <option key={s.store_id} value={s.store_id}>
                              {locationLabelForStore(s)}
                            </option>
                          ))
                        )}
                      </select>
                      </label>
                    </ClaimFieldRow>
                  </>
                ) : (
                  <ClaimFieldRow icon={<IconLocation />}>
                    <label className="claim-field-stack" htmlFor="ct-location-flat">
                      <span className="claim-field-label">Location</span>
                      <select
                        id="ct-location-flat"
                        className={`claim-select${lockPresetPickers ? " claim-select--preset-locked" : ""}`}
                        value={storeId}
                        onChange={(e) => setStoreId(e.target.value)}
                        disabled={
                          storesLoading ||
                          lockPresetPickers ||
                          !registrationStream ||
                          filteredStores.length === 0 ||
                          stores.length === 0
                        }
                        aria-describedby={lockPresetPickers ? "ct-preset-locked-hint" : undefined}
                      >
                      {!registrationStream ? (
                        <option value="">Select above first</option>
                      ) : filteredStores.length === 0 ? (
                        <option value="">No locations available</option>
                      ) : (
                        filteredStores.map((s) => (
                          <option key={s.store_id} value={s.store_id}>
                            {s.business_name}
                          </option>
                        ))
                      )}
                    </select>
                    </label>
                  </ClaimFieldRow>
                )}

                {storesLoading ? (
                  <span className="claim-muted">Loading locations…</span>
                ) : storesError ? (
                  <span className="claim-field-error" role="alert">
                    {storesError}
                  </span>
                ) : stores.length === 0 ? (
                  <div className="claim-validation-summary" role="status" aria-live="polite">
                    <p style={{ margin: 0, fontWeight: 650, color: "var(--ct-slate)" }}>
                      No locations are currently accepting self-registration.
                    </p>
                    <p style={{ margin: "0.35rem 0 0" }}>
                      Please contact your administrator to enable self-registration for your location.
                    </p>
                  </div>
                ) : registrationStream && filteredStores.length === 0 ? (
                  <span className="claim-muted">
                    No locations have been added for self-registration under this type.
                  </span>
                ) : null}

                {registrationStream !== "resident" && (
                  <ClaimFieldRow icon={<IconRole />}>
                    <label className="claim-field-stack" htmlFor="ct-role">
                      <span className="claim-field-label">Role</span>
                      <select
                        id="ct-role"
                        className={`claim-select${rolesError ? " claim-select--error" : ""}`}
                        value={catalogRoles.includes(selectedRole) ? selectedRole : ""}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        disabled={
                          storesLoading ||
                          !registrationStream ||
                          filteredStores.length === 0 ||
                          rolesLoading ||
                          catalogRoles.length === 0 ||
                          Boolean(rolesError)
                        }
                      >
                      {catalogRoles.length === 0 && !rolesLoading ? (
                        <option value="">No catalogue roles loaded</option>
                      ) : (
                        catalogRoles.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))
                      )}
                    </select>
                    {rolesLoading ? (
                      <span className="claim-muted">Loading roles from catalogue…</span>
                    ) : rolesError ? (
                      <span className="claim-field-error" role="alert">
                        {rolesError}
                      </span>
                    ) : catalogRoles.length === 0 && storeId.trim() !== "" ? (
                      <span className="claim-muted">
                        No allowed roles listed for this store in the Margaritaville catalogue.
                      </span>
                    ) : null}
                    </label>
                  </ClaimFieldRow>
                )}

                {registrationStream &&
                storeId.trim() !== "" &&
                (registrationStream === "resident" ||
                  (catalogRoles.includes(selectedRole) && selectedRole.trim() !== "")) ? (
                  <ClaimFieldRow icon={<IconRegistrationType />}>
                    <label className="claim-field-stack" htmlFor="ct-employee-type">
                      <span className="claim-field-label">Employee type *</span>
                      <select
                        id="ct-employee-type"
                        className="claim-select"
                        value={employeeType}
                        onChange={(e) =>
                          setEmployeeType(e.target.value as "hourly" | "salaried_manager")
                        }
                        aria-required
                        aria-describedby="ct-employee-type-hint"
                      >
                        <option value="salaried_manager">Salaried Manager</option>
                        <option value="hourly">Hourly</option>
                      </select>
                      <span className="claim-muted" id="ct-employee-type-hint">
                        Matches Workvivo (Salaried Manager vs Hourly). Defaults from your role; change if your
                        classification differs.
                      </span>
                    </label>
                  </ClaimFieldRow>
                ) : null}

                <hr className="claim-form-divider" />

                <ClaimFieldRow icon={<IconPerson />}>
                  <label className="claim-field-stack" htmlFor="ct-first-name">
                    <span className="claim-field-label">First name *</span>
                    <input
                      id="ct-first-name"
                      type="text"
                      className={`claim-input${fieldError("firstName") ? " claim-input--error" : ""}`}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      onBlur={() => setTouched((prev) => ({ ...prev, firstName: true }))}
                      autoComplete="given-name"
                      placeholder="Enter first name"
                      aria-required
                      aria-invalid={Boolean(fieldError("firstName"))}
                      aria-describedby={fieldError("firstName") ? "firstName-error" : undefined}
                    />
                    {fieldError("firstName") && (
                      <span id="firstName-error" className="claim-field-error" role="alert">
                        {fieldError("firstName")}
                      </span>
                    )}
                  </label>
                </ClaimFieldRow>

                <ClaimFieldRow icon={<IconPerson />}>
                  <label className="claim-field-stack" htmlFor="ct-last-name">
                    <span className="claim-field-label">Last name *</span>
                    <input
                      id="ct-last-name"
                      type="text"
                      className={`claim-input${fieldError("lastName") ? " claim-input--error" : ""}`}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      onBlur={() => setTouched((prev) => ({ ...prev, lastName: true }))}
                      autoComplete="family-name"
                      placeholder="Enter last name"
                      aria-required
                      aria-invalid={Boolean(fieldError("lastName"))}
                      aria-describedby={fieldError("lastName") ? "lastName-error" : undefined}
                    />
                    {fieldError("lastName") && (
                      <span id="lastName-error" className="claim-field-error" role="alert">
                        {fieldError("lastName")}
                      </span>
                    )}
                  </label>
                </ClaimFieldRow>

                <ClaimFieldRow icon={<IconMail />}>
                  <label className="claim-field-stack" htmlFor="ct-email">
                    <span className="claim-field-label">Preferred sign-in email *</span>
                    <input
                      id="ct-email"
                      type="email"
                      className={`claim-input${fieldError("email") ? " claim-input--error" : ""}`}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
                      autoComplete="email"
                      inputMode="email"
                      placeholder="Enter email address"
                      aria-required
                      aria-invalid={Boolean(fieldError("email"))}
                      aria-describedby={fieldError("email") ? "email-error" : undefined}
                    />
                    {fieldError("email") && (
                      <span id="email-error" className="claim-field-error" role="alert">
                        {fieldError("email")}
                      </span>
                    )}
                    <span className="claim-muted">
                      This will be used as your username and primary contact.
                    </span>
                  </label>
                </ClaimFieldRow>

                <ClaimFieldRow icon={<IconMailConfirm />}>
                  <label className="claim-field-stack" htmlFor="ct-email-confirm">
                    <span className="claim-field-label">Confirm sign-in email *</span>
                    <input
                      id="ct-email-confirm"
                      type="email"
                      className={`claim-input${fieldError("emailConfirm") ? " claim-input--error" : ""}`}
                      value={confirmEmail}
                      onChange={(e) => setConfirmEmail(e.target.value)}
                      onBlur={() => setTouched((prev) => ({ ...prev, emailConfirm: true }))}
                      autoComplete="off"
                      inputMode="email"
                      placeholder="Re-enter email address"
                      aria-required
                      aria-invalid={Boolean(fieldError("emailConfirm"))}
                      aria-describedby={fieldError("emailConfirm") ? "emailConfirm-error" : undefined}
                    />
                    {fieldError("emailConfirm") && (
                      <span id="emailConfirm-error" className="claim-field-error" role="alert">
                        {fieldError("emailConfirm")}
                      </span>
                    )}
                    <span className="claim-muted">Please enter the same email address as above.</span>
                  </label>
                </ClaimFieldRow>

                <label
                  className={`claim-confirm-row${infoConfirmed ? " claim-confirm-row--ok" : ""}${
                    fieldError("infoConfirmed") ? " claim-confirm-row--error" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={infoConfirmed}
                    onChange={(e) => setInfoConfirmed(e.target.checked)}
                    onBlur={() => setTouched((prev) => ({ ...prev, infoConfirmed: true }))}
                    aria-required
                    aria-invalid={Boolean(fieldError("infoConfirmed"))}
                    aria-describedby={fieldError("infoConfirmed") ? "infoConfirmed-error" : undefined}
                  />
                  <span className="claim-confirm-text">
                    I confirm that all the information I have entered is correct. *
                  </span>
                </label>
                {fieldError("infoConfirmed") && (
                  <span id="infoConfirmed-error" className="claim-field-error" role="alert">
                    {fieldError("infoConfirmed")}
                  </span>
                )}
              </div>

              {errorMessage && (
                <p
                  role="alert"
                  style={{
                    margin: "1rem 0 0",
                    color: "var(--ct-error-text)",
                    fontSize: "0.9rem",
                  }}
                >
                  {errorMessage}
                </p>
              )}

              <div style={{ marginTop: "1.5rem" }}>
                {!canSubmit && (submitAttempted || Object.values(touched).some(Boolean)) && (
                  <div
                    className="claim-validation-summary"
                    role="status"
                    aria-live="polite"
                    style={{ marginBottom: "0.75rem" }}
                  >
                    <p style={{ margin: 0, fontWeight: 650, color: "var(--ct-slate)" }}>
                      To submit, please:
                    </p>
                    <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.2rem" }}>
                      {validation.problems.map((p) => (
                        <li key={p.field} style={{ marginTop: "0.25rem" }}>
                          {p.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  type="submit"
                  className={`claim-submit-btn${
                    status === "submitting" || !canSubmit ? " claim-submit-btn--disabled" : ""
                  }`}
                  disabled={status === "submitting" || !canSubmit}
                >
                  {status === "submitting" ? "Submitting…" : "Submit request"}
                </button>
                {(status === "submitting" || !canSubmit) && (
                  <p className="claim-submit-foot" aria-live="polite">
                    <span aria-hidden="true">🔒</span>
                    {status === "submitting"
                      ? "Please wait while we send your request."
                      : "Complete all required fields to submit."}
                  </p>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountRequestFormPage;
